<!-- eslint-disable -->
<!--
  Karim, 27 Sep: "the opportunity card is too long, make it shorter and change
  design; colors more to blue, not yellow/orange." Four short rows instead of
  ~440px: id + expiry + flags / client / scheme / next activity + status + owner.
  The long description, sub-step and labels moved to the hover tooltip and the
  drawer (one click away) — they were repeating what the chips already say.
-->
<template>
  <div
    class="crm-card"
    :class="[
      `crm-card--p-${(card.priority || 'low').toLowerCase()}`,
      { 'crm-card--selected': isSelected },
      { 'crm-card--compact': boardSettings.density === 'compact' },
      { 'crm-card--aged': isAged }
    ]"
    :title="cardTooltip"
    @click="$emit('click', card)"
  >
    <!-- Row 1: id · expiry · flags · menu -->
    <div class="crm-card__top">
      <input
        v-if="bulkMode"
        type="checkbox"
        :checked="isSelected"
        class="crm-card__checkbox"
        @click.stop="$emit('select', card)"
      />
      <!-- Karim, 27 Sep: a renewal shows the request number it renews, not R-CERT-…. -->
      <span
        class="crm-card__id"
        :title="card.requestCode || card.renewingRequestCode ? `Internal id: ${card.id}` : null"
      >{{ card.requestCode || (card.renewingRequestCode ? `Renew ${card.renewingRequestCode}` : card.id) }}</span>
      <span
        v-if="activePipeline === 'renewals' && card.remainingDays !== undefined && card.remainingDays !== null"
        class="crm-chip"
        :class="card.remainingDays < 0 ? 'crm-chip--late' : (card.remainingDays <= 30 ? 'crm-chip--soon' : 'crm-chip--ok')"
        :title="card.remainingDays < 0 ? `Expired ${-card.remainingDays} days ago` : `Expires in ${card.remainingDays} days`"
      >
        <feather-icon icon="ClockIcon" size="10" />
        {{ card.remainingDays < 0 ? `${-card.remainingDays}d ago` : `${card.remainingDays}d` }}
      </span>
      <span class="crm-card__flags">
        <!-- Karim, 7 Sep: Reminder / Concern / Needs-Update flags. -->
        <feather-icon v-if="card.reminder" icon="BellIcon" size="12" class="crm-flag" title="Reminder set" />
        <feather-icon v-if="hasConcern" icon="AlertTriangleIcon" size="12" class="crm-flag" title="Client has a concern" />
        <feather-icon v-if="card.needsUpdate" icon="MessageSquareIcon" size="12" class="crm-flag" title="Urgent update requested from sales" />
      </span>
      <div class="dropdown b-dropdown crm-card__menu-wrap" @click.stop>
        <button type="button" class="crm-card__menu-btn" title="Card options" @click="toggleMenu">
          <feather-icon icon="MoreVerticalIcon" size="14" />
        </button>
          <div v-if="showMenu" class="crm-card__menu shadow-lg">
            <!--
              Karim, 7 Sep: "when the client is new I cannot create activity."
              Root cause: the only way into ActivityModal was the Next Activity
              pill, which only renders once a next activity already exists — a
              brand-new opportunity fresh off a lead (no customer link yet, no
              activity history yet) has no pill and therefore no way to open
              it at all. These two entries are always available regardless.
            -->
            <button type="button" class="crm-card__menu-item" @click="handleAction('schedule-activity')">
              <feather-icon icon="ClockIcon" size="13" class="mr-1" /> Log Activity
            </button>
            <button type="button" class="crm-card__menu-item" @click="handleAction('email')">
              <feather-icon icon="MailIcon" size="13" class="mr-1" /> Send Email
            </button>
            <div class="dropdown-divider my-1"></div>
            <button type="button" class="crm-card__menu-item" @click="handleAction('assign')">
              <feather-icon icon="UserPlusIcon" size="13" class="mr-1" /> Assign Team Member
            </button>
            <button type="button" class="crm-card__menu-item" @click="handleAction('priority')">
              <feather-icon icon="AlertCircleIcon" size="13" class="mr-1" /> Set Priority
            </button>
            <button type="button" class="crm-card__menu-item" @click="handleAction('label')">
              <feather-icon icon="TagIcon" size="13" class="mr-1" /> Add Label
            </button>
            <div class="dropdown-divider my-1"></div>
            <!-- Karim, 7 Sep: Reminder / Concern / Notes quick-actions. -->
            <button type="button" class="crm-card__menu-item" @click="handleAction('reminder')">
              <feather-icon icon="BellIcon" size="13" class="mr-1" />
              {{ card.reminder ? 'Clear Reminder' : 'Set Reminder' }}
            </button>
            <button type="button" class="crm-card__menu-item" @click="handleAction('concern')">
              <feather-icon icon="AlertTriangleIcon" size="13" class="mr-1" />
              {{ hasConcern ? 'Clear Concern' : 'Mark Concern' }}
            </button>
            <button type="button" class="crm-card__menu-item" @click="handleAction('needs-update')">
              <feather-icon icon="MessageSquareIcon" size="13" class="mr-1" />
              {{ card.needsUpdate ? 'Clear Update Request' : 'Request Urgent Update' }}
            </button>
            <div class="dropdown-divider my-1"></div>
            <button type="button" class="crm-card__menu-item text-danger" @click="handleAction('delete')">
              <feather-icon icon="Trash2Icon" size="13" class="mr-1" /> Delete Item
            </button>
          </div>
        </div>
    </div>

    <!-- Row 2: who -->
    <h4 class="crm-card__title">
      <router-link
        v-if="card.clientId && card.client"
        to="/importers"
        class="crm-card__title-link"
        @click.native.stop
      >{{ card.client }}</router-link>
      <template v-else>{{ card.client || card.title }}</template>
    </h4>

    <!-- Row 3: what -->
    <div v-if="subtitle" class="crm-card__sub">
      <span class="crm-card__scheme">{{ subtitle }}</span>
      <span v-if="card.type" class="crm-card__type">{{ card.type }}</span>
    </div>

    <!-- Row 4: next step · status · value · owner -->
    <div class="crm-card__foot">
      <button
        v-if="card.nextActivity"
        type="button"
        class="crm-next"
        :class="{ 'crm-next--late': isNextActivityOverdue }"
        :title="`Next activity: ${card.nextActivity.type}${card.nextActivity.note ? ' — ' + card.nextActivity.note : ''} (click to complete)`"
        @click.stop="$emit('quick-action', { card, action: 'schedule-activity' })"
      >
        <feather-icon :icon="nextActivityIcon" size="11" />
        {{ formatNextActivityDate(card.nextActivity.dueDate) }}
      </button>

      <span
        v-if="card.decision"
        class="crm-status"
        :class="card.decision.status === 'renewed' ? 'crm-status--ok' : 'crm-status--bad'"
        :title="card.decision.reason ? `Reason: ${card.decision.reason}` : null"
      >{{ card.decision.status === 'renewed' ? 'Renewed' : 'Rejected' }}</span>
      <!-- Karim, 3 Sep: last request status — raw Jeem/Saber text on hover. -->
      <span
        v-else-if="card.lastRequestStatusLabel"
        class="crm-status"
        :class="statusClass"
        :title="`Last request (Jeem/Saber): ${card.lastRequestStatus}`"
      >{{ card.lastRequestStatusLabel }}</span>

      <span
        v-if="boardSettings.showDueDates && card.dueDate"
        class="crm-card__due"
        :class="{ 'crm-card__due--late': isOverdue }"
        :title="isOverdue ? 'Overdue' : 'Due date'"
      >
        <feather-icon icon="CalendarIcon" size="10" /> {{ formatDate(card.dueDate) }}
      </span>

      <span
        v-if="activePipeline !== 'tickets' && boardSettings.showValues && card.value > 0"
        class="crm-card__value"
      >{{ card.currency || 'AED' }} {{ formatCurrency(card.value) }}</span>

      <!-- MID-CRM P2.3 — the one responsible salesperson; unassigned is shown, not hidden. -->
      <span v-if="boardSettings.showAssignees" class="crm-card__owner">
        <span
          v-if="card.owner"
          class="crm-avatar-sm"
          :title="`Salesperson: ${card.owner}`"
          :style="{ backgroundColor: avatarColor(card.owner) }"
        >{{ initials(card.owner) }}</span>
        <span v-else class="crm-avatar-sm crm-avatar-sm--none" title="Unassigned — no salesperson is responsible">
          <feather-icon icon="UserXIcon" size="11" />
        </span>
      </span>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment'

export default {
  name: 'KanbanCard',
  props: {
    card: { type: Object, required: true },
    activePipeline: { type: String, required: true },
    boardSettings: { type: Object, required: true },
    bulkMode: { type: Boolean, default: false },
    isSelected: { type: Boolean, default: false }
  },
  data() {
    return {
      showMenu: false
    }
  },
  computed: {
    hasConcern() {
      return !!(this.card.labels && this.card.labels.some(l => l.name === 'Concern'))
    },
    statusClass() {
      const map = { issued: 'crm-status--ok', rejected: 'crm-status--bad', in_progress: 'crm-status--info' }
      return map[this.card.lastRequestStatusNormalized] || ''
    },
    /** Scheme first; the generated title only when there is no scheme. */
    subtitle() {
      return this.card.serviceScheme || (this.card.client ? this.card.title : '') || ''
    },
    /** Everything that left the card face, on hover. */
    cardTooltip() {
      const parts = [this.card.title]
      if (this.card.subStage) parts.push(`Step: ${this.card.subStage}`)
      if (this.card.description) parts.push(String(this.card.description).replace(/\*\*/g, ''))
      const labels = (this.card.labels || []).map(l => l.name).filter(Boolean)
      if (labels.length) parts.push(`Labels: ${labels.join(', ')}`)
      return parts.filter(Boolean).join('\n')
    },
    // MID-CRM P1.3 — `assigneesList` removed. It read `card.assignees`, a field no
    // endpoint ever returns because there is no assignees table; the card face now
    // renders `card.owner` directly (the one real owner).
    // MID-CRM P1.5 — checklistTotal / checklistDone removed with the checklist
    // feature: `card.checklist` is not a column and no endpoint returns it, so the
    // progress bar on the card face could only ever read 0/0.
    isOverdue() {
      if (!this.card.dueDate) return false
      return moment(this.card.dueDate).isBefore(moment(), 'day')
    },
    nextActivityIcon() {
      if (!this.card.nextActivity) return 'CalendarIcon'
      const type = this.card.nextActivity.type
      const map = { Call: 'PhoneIcon', Email: 'MailIcon', Visit: 'MapPinIcon', Meeting: 'UsersIcon', 'Follow-up': 'ClockIcon' }
      return map[type] || 'CalendarIcon'
    },
    isNextActivityOverdue() {
      if (!this.card.nextActivity || !this.card.nextActivity.dueDate) return false
      return moment(this.card.nextActivity.dueDate).isBefore(moment(), 'day')
    },
    isAged() {
      if (!this.boardSettings.cardAging || !this.card.stageChangedAt) return false
      const daysInStage = moment().diff(moment(this.card.stageChangedAt), 'days')
      return daysInStage >= (this.boardSettings.cardAgingDays || 14)
    }
  },
  mounted() {
    document.addEventListener('click', this.closeMenu)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu)
  },
  methods: {
    toggleMenu() {
      this.showMenu = !this.showMenu
    },
    closeMenu() {
      this.showMenu = false
    },
    handleAction(action) {
      this.showMenu = false
      this.$emit('quick-action', { card: this.card, action })
    },
    formatCurrency(val) {
      if (!val) return '0'
      return Number(val).toLocaleString()
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('MMM D')
    },
    formatNextActivityDate(dateStr) {
      if (!dateStr) return 'Scheduled'
      const m = moment(dateStr)
      if (m.isSame(moment(), 'day')) return 'Today'
      if (m.isSame(moment().add(1, 'day'), 'day')) return 'Tomorrow'
      return m.format('MMM D')
    },
    initials(name) {
      if (!name) return 'U'
      const parts = name.split(' ')
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.slice(0, 2).toUpperCase()
    },
    avatarColor(name) {
      const colors = ['#1d4ed8', '#2563eb', '#0284c7', '#0891b2', '#1d4ed8', '#1e40af', '#0369a1']
      let hash = 0
      for (let i = 0; i < (name || '').length; i++) hash += name.charCodeAt(i)
      return colors[Math.abs(hash) % colors.length]
    }
  }
}
</script>
<style lang="scss" scoped>
$blue-900: #1e3a8a;
$blue-700: #1d4ed8;
$blue-600: #2563eb;
$blue-500: #3b82f6;
$blue-300: #93c5fd;
$blue-100: #dbeafe;
$blue-50: #eff6ff;
$green: #059669;
$red: #dc2626;
$slate-100: #f1f5f9;
$slate-200: #e2e8f0;
$slate-400: #94a3b8;
$slate-500: #64748b;
$slate-700: #334155;
$slate-900: #0f172a;

.crm-card {
  position: relative;
  background: #fff;
  border: 1px solid $slate-200;
  border-left: 3px solid $blue-300;
  border-radius: 8px;
  padding: 8px 10px 8px 10px;
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease;

  &:hover { border-color: $blue-500; box-shadow: 0 3px 10px rgba($blue-900, .10); }

  // Priority as the left edge only — blue scale, darker = more urgent.
  &--p-high { border-left-color: $blue-900; }
  &--p-medium { border-left-color: $blue-600; }
  &--p-low { border-left-color: $blue-300; }

  &--selected { border-color: $blue-600; box-shadow: 0 0 0 2px rgba($blue-600, .25); }
  &--compact { padding: 6px 8px; }
  &--aged { background: #f8fafc; }
}

.crm-card__top {
  display: flex; align-items: center; gap: 6px; min-height: 20px;
}
.crm-card__checkbox { margin: 0; }
.crm-card__id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10.5px; font-weight: 600; color: $blue-700;
  background: $blue-50; border-radius: 4px; padding: 1px 6px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 130px;
}
.crm-chip {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 10.5px; font-weight: 600; border-radius: 4px; padding: 1px 5px; white-space: nowrap;
  &--ok { background: $blue-50; color: $blue-700; }
  &--soon { background: $blue-100; color: $blue-900; }
  &--late { background: #fef2f2; color: $red; }
}
.crm-card__flags { display: inline-flex; gap: 3px; color: $red; }
.crm-card__menu-wrap { margin-left: auto; position: relative; }
.crm-card__menu-btn {
  border: 0; background: transparent; color: $slate-400; padding: 2px; border-radius: 4px; line-height: 0; cursor: pointer;
  &:hover { background: $slate-100; color: $slate-700; }
}
.crm-card__menu {
  position: absolute; right: 0; top: 22px; z-index: 30; min-width: 190px;
  background: #fff; border: 1px solid $slate-200; border-radius: 8px; padding: 4px;
}
.crm-card__menu-item {
  display: flex; align-items: center; width: 100%; border: 0; background: transparent;
  font-size: 12.5px; color: $slate-700; padding: 6px 8px; border-radius: 6px; text-align: left; cursor: pointer;
  &:hover { background: $blue-50; color: $blue-700; }
}

.crm-card__title {
  margin: 5px 0 1px; font-size: 13px; font-weight: 700; line-height: 1.3; color: $slate-900;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.crm-card__title-link { color: $slate-900; &:hover { color: $blue-700; text-decoration: none; } }

.crm-card__sub {
  display: flex; align-items: center; gap: 6px; font-size: 11.5px; color: $slate-500; min-width: 0;
}
.crm-card__scheme { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.crm-card__type { flex: none; text-transform: capitalize; color: $blue-700; font-weight: 600; font-size: 10.5px; }

.crm-card__foot {
  display: flex; align-items: center; flex-wrap: wrap; gap: 5px; margin-top: 7px;
}
.crm-next {
  display: inline-flex; align-items: center; gap: 3px; border: 1px solid $blue-100; background: $blue-50;
  color: $blue-700; font-size: 10.5px; font-weight: 600; border-radius: 999px; padding: 1px 7px; cursor: pointer;
  &:hover { border-color: $blue-500; }
  &--late { background: #fef2f2; border-color: #fecaca; color: $red; }
}
.crm-status {
  font-size: 10.5px; font-weight: 600; border-radius: 999px; padding: 1px 7px;
  background: $slate-100; color: $slate-500; white-space: nowrap;
  &--ok { background: #ecfdf5; color: $green; }
  &--bad { background: #fef2f2; color: $red; }
  &--info { background: $blue-50; color: $blue-700; }
}
.crm-card__due {
  display: inline-flex; align-items: center; gap: 3px; font-size: 10.5px; color: $slate-500;
  &--late { color: $red; font-weight: 600; }
}
.crm-card__value { font-size: 11px; font-weight: 700; color: $blue-900; }
.crm-card__owner { margin-left: auto; }
.crm-avatar-sm {
  width: 20px; height: 20px; border-radius: 50%; color: #fff; font-size: 9px; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
  &--none { background: $slate-100; color: $slate-400; border: 1px dashed $slate-400; }
}
</style>
